<template>
    <div class="p-6">
      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center">
          <h2 class="text-xl font-semibold text-primary mr-3 cursor-pointer"  
          @click="$route.meta.patientDetailPathName ? $router.push({name: $route.meta.patientDetailPathName}) : null">{{ patientName }}</h2>
        </div>
        <vs-button
          icon-pack="feather"
          icon="icon-plus"
          icon-before
          class="font-semibold"
          @click="showUploadDialog = true"
        >
          Add file
        </vs-button>
      </div>
  
      <vs-table
        :data="patientFiles"
        noDataText="No files found"
        class="w-full bg-white rounded-lg shadow-sm border border-gray-200"
      >
        <template slot="thead">
          <vs-th>File</vs-th>
          <vs-th>Date uploaded</vs-th>
          <vs-th>Uploaded by</vs-th>
          <vs-th>Actions</vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr :key="indextr" v-for="(tr, indextr) in data" :data="tr" class="hover:bg-gray-50 border-b border-gray-200">
            <vs-td class="whitespace-nowrap">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded flex items-center justify-center flex-shrink-0">
                  <vs-icon 
                    icon-pack="feather" 
                    :icon="getFileIcon(tr.type)" 
                    size="small"
                  ></vs-icon>
                </div>
                <span class="text-sm font-medium text-gray-900">{{ tr.name }}</span>
              </div>
            </vs-td>
            <vs-td class="whitespace-nowrap">
              <span class="text-sm text-gray-900">{{ formatFileDate(tr.uploadedAt) }}</span>
            </vs-td>
            <vs-td class="whitespace-nowrap">
              <span class="text-sm text-gray-900">{{ tr.uploadedBy }}</span>
            </vs-td>
            <vs-td class="whitespace-nowrap">
              <div class="flex items-center space-x-2">
                <vs-button
                type="border"
                size="small"
                @click="openFile(tr)"
                icon-pack="feather"
                class="mr-2"
                >View</vs-button
              >
              
                <vs-button
                  type="flat"
                  size="small"
                  color="danger"
                  icon-pack="feather"
                  icon="icon-trash-2"
                  class="transition-colors duration-200"
                  @click="deleteFile(tr)"
                />
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
  
      <PatientFileUploadDialog 
        :open="showUploadDialog"
        :isUploading="isUploading"
        @update:open="showUploadDialog = $event"
        @close="handleDialogClose"
        @refetchPatientFiles="loadPatientFiles"
      />
  
      <PatientFileDeleteDialog
        :open="showDeleteDialog"
        :fileName="fileToDelete ? fileToDelete.name : ''"
        :fileDate="fileToDelete ? formatFileDate(fileToDelete.uploadedAt) : ''"
        @close="closeDeleteDialog"
        @confirm-delete="confirmFileDeletion"
      />
    </div>
  </template>
  
<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import PatientFileUploadDialog from './PatientFileUploadDialog.vue';
import PatientFileDeleteDialog from './PatientFileDeleteDialog.vue';
  
export default {
  name: 'PatientFiles',
  components: {
    PatientFileUploadDialog,
    PatientFileDeleteDialog,
  },
  data() {
    return {
      patientFiles: [],
      loadingFiles: false,
      showUploadDialog: false,
      isUploading: false,
      showDeleteDialog: false,
      fileToDelete: null,
      patientName: '',
    };
  },
  methods: {
    ...mapActions('patient', ['getPatientFiles', 'deletePatientFile']),
      
    async loadPatientFiles() {
      this.loadingFiles = true;
      try {
        const patientId = this.$route.query.patientId || this.$route.params.patientId;
        if (!patientId) {
          console.error('No patientId found in route');
          this.patientFiles = [];
          return;
        }
        const response = await this.getPatientFiles(patientId);
        this.patientFiles = response.data.data.files || [];
        this.patientName = response.data.data.fullName || '';
      } catch (error) {
        this.patientFiles = [];
        console.error('Failed to load patient files:', error);
      } finally {
        this.loadingFiles = false;
      }
    },
      
    formatFileDate(dateString) {
      return moment(dateString).format('DD MMM YYYY');
    },
      
    getFileIcon(fileType) {
      const imageTypes = ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp'];
      if (imageTypes.includes(fileType)) {
        return 'icon-image';
      }
      return 'icon-file-text';
    },

    openFile(file) {
      window.open(file.url, '_blank');
    },
      
    deleteFile(file) {
      this.fileToDelete = file;
      this.showDeleteDialog = true;
    },
      
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.fileToDelete = null;
    },
      
    async confirmFileDeletion() {
      try {
        await this.deletePatientFile(this.fileToDelete._id);
          
        this.$vs.notify({
          title: 'Success',
          text: 'File deleted successfully',
          color: 'success',
        });
          
        this.closeDeleteDialog();
        this.loadPatientFiles();
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Failed to delete file',
          color: 'danger',
        });
      }
    },
      
    handleDialogClose() {
      this.showUploadDialog = false;
      this.isUploading = false;
    },
  },
  async created() {
    await this.loadPatientFiles();
  },
};
</script>